// Custom (special-order) moulding — Michael 2026-09-22. STAFF ONLY ($xxlAdmin).
//
// Staff pick any standard stand-in frame for the treatment (Black / White / Weathered /
// Dark Walnut …; the 3D view keeps showing it), tick "Custom moulding" and enter the
// manufacturer, item # and our cut & join cost. The moulding is ordered in lengths and
// cut in-house, so the work order and cut list are unchanged apart from the name.
// Price (backend utils/pricing/customMouldingPricing.ts, same rule as the quote tool):
// the stand-in's treatment price + our cost x 2, one number; Deep / Maple surcharges
// dropped; not XXL-scaled. Our cost and the x2 never print anywhere a customer reads.
//
// Stored on the visualizer options as frameOptions.customMoulding = { mfr, item, cost }
// (label stays the stand-in so the frame renders and restores). createLineItem writes the
// cart line with label = customMouldingLabel() and customMoulding.standIn = the stand-in's
// label; restoreVisualizerState puts the stand-in back on Edit / "Same specs".
// Custom MAT (matted / mat-only) and custom BACKING (float) follow the same protocol (Michael
// 2026-09-22): stand-in colour stays showing, manufacturer / item # / our cost entered, + cost x 2.
// Stored at frameOptions.matboard.custom and frameOptions.backing.custom. Spacers are never custom.
export type CustomMoulding = {
  mfr: string;
  item: string;
  /** Our cut & join cost from the manufacturer; the backend bills x 2. "" = not priced yet. */
  cost: number | "";
  /** The stand-in frame's label, written on the cart line only. */
  standIn?: string;
};

export const EMPTY_CUSTOM_MOULDING: CustomMoulding = { mfr: "", item: "", cost: "" };

/** The line's custom moulding, or null. */
export function customMouldingOf(frameOptions: any): CustomMoulding | null {
  const cm = frameOptions?.customMoulding;
  if (!cm || typeof cm !== "object") return null;
  const cost = parseFloat(cm.cost);
  return {
    mfr: String(cm.mfr ?? "").trim(),
    item: String(cm.item ?? "").trim(),
    cost: Number.isFinite(cost) && cost > 0 ? cost : "",
    ...(cm.standIn ? { standIn: String(cm.standIn) } : {}),
  };
}

/** `custom Larson Juhl 12345 (special order)` — mirrors the backend / quote tool wording. */
export function customMouldingLabel(cm: CustomMoulding): string {
  const who = [cm.mfr, cm.item].filter(Boolean).join(" ");
  return `custom${who ? ` ${who}` : " moulding"} (special order)`;
}

export const isCustomMouldingLabel = (label: unknown): boolean =>
  /^custom\b.*\(special order\)\s*$/i.test(String(label ?? ""));

/** Framed treatments only — the visualizer keeps frameOptions while on Print Only / Mat Only. */
export function customMouldingApplies(options: any): boolean {
  if (!options?.display || options?.frameOptions?.matboard?.only) return false;
  const t = String(options?.treatmentOptions?.type ?? "");
  if (/only|no frame|stretcher|loose|panel|regular/i.test(t)) return false;
  return /straight|matted|shadow|float|frame/i.test(t);
}

/** Alias — the same shape serves moulding, mat and backing. */
export type CustomPart = CustomMoulding;

export function customPartOf(raw: any): CustomPart | null {
  if (!raw || typeof raw !== "object") return null;
  const cost = parseFloat(raw.cost);
  return {
    mfr: String(raw.mfr ?? "").trim(),
    item: String(raw.item ?? "").trim(),
    cost: Number.isFinite(cost) && cost > 0 ? cost : "",
    ...(raw.standIn ? { standIn: String(raw.standIn) } : {}),
  };
}

/** `custom Decor C9876 (special order)` — used as the mat / backing colour name on the line. */
export const customPartLabel = customMouldingLabel;

/** Matted & Framed / Mat Only only. */
export function customMatOf(options: any): CustomPart | null {
  const t = String(options?.treatmentOptions?.type ?? "");
  const matOnly = !!options?.frameOptions?.matboard?.only || /mat only/i.test(t);
  if (!matOnly && (!options?.display || !/mat/i.test(t))) return null;
  if (/no frame|print only/i.test(t)) return null;
  return customPartOf(options?.frameOptions?.matboard?.custom);
}

/** Float Mount only. */
export function customBackingOf(options: any): CustomPart | null {
  if (!options?.display || options?.frameOptions?.matboard?.only) return null;
  if (!/float/i.test(String(options?.treatmentOptions?.type ?? ""))) return null;
  return customPartOf(options?.frameOptions?.backing?.custom);
}
